// Deep import: `defineProvider` is not re-exported from the providers barrel,
// which only surfaces `getDefinedProvider` and `listProviders`. The package's
// `./libs/*` subpath pattern resolves nested paths, so this needs no change to
// the shared package.
import { defineProvider } from '@proj-airi/stage-ui/libs/providers/providers/registry'
import { createOpenAI } from '@xsai-ext/providers/create'
import { z } from 'zod'

import { tutorLlmEndpoint, tutorLlmProviderId } from '../constants/llm-endpoint'

interface TutorLlmConfig {
  apiKey: string
  baseUrl: string
}

/**
 * Reasoning effort sent on every request.
 *
 * The pinned server runs Ollama behind an auth proxy, and its models reason by
 * default: `gemma4:26b` answering "개구리 새끼는 모야?" spends 143 completion
 * tokens and 3.3s, of which 138 tokens are reasoning the student never sees.
 * With effort set to none the same answer costs 5 tokens and 0.6s.
 *
 * Ollama's own `think: false` is *not* an option here: on the OpenAI-compatible
 * `/v1` route the server ignores it (measured: 230 reasoning characters with
 * `think: false`, 0 with `reasoning_effort: "none"`). That is why this provider
 * exists instead of configuring the built-in `ollama` one.
 */
const reasoningEffort = 'none'

const configSchema = z.object({
  apiKey: z.string().default(tutorLlmEndpoint.apiKey),
  baseUrl: z.string().default(tutorLlmEndpoint.baseUrl),
})

/**
 * Registers aiTuter's pinned chat/vision provider.
 *
 * `defineProvider` writes into a module-level registry as a side effect, so this
 * must be imported for effect before any store lists providers — see the import
 * in `main.ts`.
 *
 * The chat options are extended the way the built-in Ollama provider extends
 * its own: extra keys on the object returned by `chat()` are forwarded into the
 * request body by xsAI.
 */
export function registerTutorLlmProvider(): void {
  defineProvider<TutorLlmConfig>({
    id: tutorLlmProviderId,
    order: 0,
    name: 'aiTuter LLM',
    description: 'Pinned tutoring endpoint. Chat and vision.',
    // Not in `packages/i18n`, so the localizers return the literal name rather
    // than a lookup key that would render as the key itself.
    nameLocalize: () => 'aiTuter LLM',
    descriptionLocalize: () => '고정된 튜터 엔드포인트. 대화와 이미지 인식.',
    tasks: ['chat'],
    icon: 'i-lobe-icons:ollama',

    createProviderConfig: () => configSchema,

    createProvider(config: TutorLlmConfig) {
      const provider = createOpenAI(
        config.apiKey || tutorLlmEndpoint.apiKey,
        config.baseUrl || tutorLlmEndpoint.baseUrl,
      )

      return {
        ...provider,
        chat(model: string) {
          return { ...provider.chat(model), reasoning_effort: reasoningEffort }
        },
      }
    },

    // Nothing for the student to fill in, so there is nothing to validate before
    // the first request.
    validationRequiredWhen: () => false,
  })
}
